import { useMemo } from 'react';
import { HistoryEvents } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/History/HistoryEvents';
import { HistoryEventResult } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/types';
import { useDocumentWorkflowEvents } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';
import type { DocumentWorkflowEventLogEntity } from '@dealstack/shared/lib/backendClient';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useDownloadHistoryDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/History/useDownloadHistoryDocument';
import { toEventDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/History/HistoryEvents.utils';
import {
	formatBrowserInfo,
	resolveActor,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ActivityTab.utils';

interface Props {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
}

const mapDocumentWorkflowEventToHistoryEvent = (
	event: DocumentWorkflowEventLogEntity
): HistoryEventResult => ({
	action: event.eventType,
	actor: resolveActor(event.userAction),
	browserInfo: formatBrowserInfo(event.userAction.userSession),
	timestamp: formatDateToUI(new Date(event.createdAt), DATE_FORMATS.UI_LONG),
	document: toEventDocument(event.file),
	priorDocument: toEventDocument(event.previousFile),
	status: event.status,
	// Invite and reminder events carry no comment, only a statusMessage holding the
	// recipient's email — the long-standing fallback, kept as-is.
	comment: event.comment ?? event.statusMessage ?? null,
	signingMethodLabel: event.signingMethodLabel ?? null,
	complianceLabel: event.complianceLabel ?? null,
});

export const ActivityTab = ({ dealId, programmeId, documentWorkflowInstanceId }: Props) => {
	const { data: eventsData, isLoading } = useDocumentWorkflowEvents({
		programmeId,
		documentWorkflowInstanceId,
	});

	const handleDocumentClick = useDownloadHistoryDocument(dealId);

	const historyEvents = useMemo(
		() => eventsData?.map(mapDocumentWorkflowEventToHistoryEvent),
		[eventsData]
	);

	if (isLoading) {
		return <InlineLoader />;
	}

	return <HistoryEvents historyEvents={historyEvents} onDocumentClick={handleDocumentClick} />;
};
